import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FieldState, FormState } from 'formstate';

import { DropdownFieldState, InputFieldState } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';

import { SiteMode, environment } from '../../api';
import {
    ApiGroup,
    ApiScope,
    AppApprovalStatus,
    AppAvailability,
    AppCategory,
    AppDetailsModel,
    APP_API_TOKEN,
    DeveloperType,
    EmptyAppAvailability,
    IAppApi,
    AppBuilder,
    SecretManagementOption,
    WebhookEvent,
    WebhookEventModel,
} from '../../api/app.api';
import { NotificationCenterStore } from '../../common';
import {
    AppScopesVersionViewModel,
    createAppScopesVersionViewModel,
} from '../models/app-scopes-version-view-model';
import { getDisplayNamesString, setsEqual, validateEmailAddress, validateUrl } from '../utils';

declare global {
    interface Crypto {
        randomUUID(): string; // not available until TypeScript 4.6
    }

    interface ReadonlySetLike<T> {
        readonly size: number;
        keys(): Iterator<T>;
        has(value: T): boolean;
    }

    interface Set<T> {
        union<U>(other: ReadonlySetLike<U>): Set<T | U>;
        difference<U>(other: ReadonlySetLike<U>): Set<T>;
    }
}

type DropdownOption<TValue> = { text?: string; value?: TValue } | undefined;

const isTenantApisSiteMode = environment.site.mode === SiteMode.TenantApis;
const validateName = (value: string) => !value.trim() && 'Enter a name.';
const validateDescription = (value: string) => !value.trim() && 'Enter a description.';
const validateAppCategoryId = (value: number | null) => value === null && 'Select a category.';
const validateAppBuilder = (value: AppBuilder | null) => value === null && 'Select an option.';
const validateGuid = (value: string) =>
    !/^\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?\s*$/i.test(value) &&
    'Enter a valid GUID.';
const validateDropdown = (value: DropdownOption<string>) => !value && 'Select an option.';
const validateSignatureKey = (value: string, isReadonly: boolean) =>
    // do not validate when key is readonly
    !isReadonly &&
    // empty string is a valid value for HMAC key, so we need to check for undefined
    value &&
    !/^.{32,64}$/.test(value) &&
    'HMAC key must be between 32 and 64 characters.';

/**
 * The banner shown at the top of the App Details page. At most one banner is shown at a time
 */
export enum AppDetailsBanner {
    None,
    Created,
    RegistrationRequestReceived,
    ThirdPartyRegistrationRequestReceived,
}

@injectable()
export class AppDetailsPageStore {
    @observable id = 0;
    @observable publicId = '';
    @observable developerType = DeveloperType.ThirdParty;
    @observable.ref scopesVersions: AppScopesVersionViewModel[] = [];
    @observable applicationKey1 = '';
    @observable.ref appCategories: AppCategory[] = [];
    @observable.ref apiGroups: ApiGroup[] = [];
    @observable.ref apiScopeMap = new Map<string, ApiScope>();
    @observable.ref webhookEventMap = new Map<string, WebhookEvent>();
    @observable.ref enabledAuthScopes = new Set<string>();
    @observable.ref existingBlockedAuthScopes = new Set<string>();
    @observable.ref savedAuthScopes = new Set<string>();
    @observable.ref savedWebhookEventNames = new Set<string>();
    @observable readonly form = new FormState({
        appName: new InputFieldState<string>('')
            .setAutoValidationDefault(false)
            .validators(validateName),
        organizationName: new InputFieldState<string>('')
            .setAutoValidationDefault(false)
            .validators(validateName),
        homepageUrl: new InputFieldState<string>('')
            .setAutoValidationDefault(false)
            .validators(validateUrl),
        emailAddress: new InputFieldState<string>('')
            .setAutoValidationDefault(false)
            .validators(...(isTenantApisSiteMode ? [validateEmailAddress] : [])),
        description: new InputFieldState<string>('')
            .setAutoValidationDefault(false)
            .validators(...(isTenantApisSiteMode ? [validateDescription] : [])),
        appCategoryId: new FieldState<number | null>(null)
            .setAutoValidationDefault(false)
            .validators(...(isTenantApisSiteMode ? [validateAppCategoryId] : [])),
        appBuilder: new FieldState<AppBuilder | null>(null).setAutoValidationDefault(false),
        // eslint-disable-next-line no-restricted-globals
        externalDataGuid: new InputFieldState<string>(crypto.randomUUID())
            .setAutoValidationDefault(false)
            .validators(validateGuid),
        apiGroup: new DropdownFieldState<DropdownOption<string>>(undefined)
            .setAutoValidationDefault(false)
            .validators(validateDropdown),
        authScopes: new FieldState(new Set<string>()).setAutoValidationDefault(false),
        webhookEvents: new FieldState([] as WebhookEventModel[]),
        webhookSignatureKey: new InputFieldState<string>('').setAutoValidationDefault(false),
        appAvailabilities: new FieldState<AppAvailability[]>([
            EmptyAppAvailability,
        ]).setAutoValidationDefault(false),
        networkAppAvailabilities: new FieldState<AppAvailability[]>([
            EmptyAppAvailability,
        ]).setAutoValidationDefault(false),
        secretManagementOption: new InputFieldState<SecretManagementOption>(
            SecretManagementOption.Developer,
        ).setAutoValidationDefault(false),
    });
    @observable deleted = false;
    @observable approvalStatus = AppApprovalStatus.NotStarted;

    @observable appJustCreated = false;
    @observable deleteDialogVisible = false;
    @observable deleting = false;
    @observable confirmScopeRemovalDialogVisible = false;
    @observable.ref removedExistingBlockedAuthScopes = { read: '', write: '' };
    @observable errorBannerVisible = false;
    @observable loading = true;
    @observable saving = false;
    @observable isWebhookSignatureKeyReadonly = false;

    constructor(
        @inject(APP_API_TOKEN) private readonly appApi: IAppApi,
        @inject(NotificationCenterStore)
        private readonly notificationCenterStore: NotificationCenterStore,
    ) {
        this.form.$.authScopes.validators(
            value =>
                !value.size &&
                !this.form.$.webhookEvents.value.length &&
                (this.selectedApiGroupHasWebhooks
                    ? 'Add at least one API scope or webhook event.'
                    : 'Add at least one API scope.'),
        );
        this.form.$.webhookSignatureKey.validators(value =>
            validateSignatureKey(value, this.isWebhookSignatureKeyReadonly),
        );
        /*
         * The app builder is only collected for Customer Apps; for other developer types the field
         * is hidden and submitted as null, so it must not be required.
         */
        this.form.$.appBuilder.validators(
            value => this.developerType === DeveloperType.Customer && validateAppBuilder(value),
        );
        this.form.$.appAvailabilities.validators(
            value =>
                this.selectedApiGroup &&
                value
                    .concat(this.form.$.networkAppAvailabilities.value)
                    .every(appAvailability => !appAvailability.resourceOwner.trim()) &&
                `Enter at least one ${this.selectedApiGroup.resourceOwnerTerm} ID` +
                    (this.selectedApiGroup.name === 'tenant' ? ' or Network ID' : '') +
                    '.',
        );
        makeObservable(this);
    }

    /**
     * The banner to show at the top of the page. Customer apps and certified third-party apps
     * (Tenant APIs only) go through an approval flow and start unapproved; their respective
     * registration-request-received banners show until the app is approved or rejected, and take
     * precedence over the "app created" banner. Other developer types and approved/rejected apps
     * fall back to the "app created" banner (shown right after creation).
     */
    @computed
    get visibleBanner(): AppDetailsBanner {
        const isPendingApproval =
            this.id > 0 &&
            this.approvalStatus !== AppApprovalStatus.Approved &&
            this.approvalStatus !== AppApprovalStatus.Rejected;
        if (isPendingApproval) {
            if (this.developerType === DeveloperType.Customer) {
                return AppDetailsBanner.RegistrationRequestReceived;
            }
            if (isTenantApisSiteMode && this.developerType === DeveloperType.ThirdParty) {
                return AppDetailsBanner.ThirdPartyRegistrationRequestReceived;
            }
        }
        if (this.appJustCreated) {
            return AppDetailsBanner.Created;
        }
        return AppDetailsBanner.None;
    }

    @computed
    get selectedApiGroup(): ApiGroup | undefined {
        return this.apiGroups.find(apiGroup => apiGroup.name === this.form.$.apiGroup.value?.value);
    }

    /**
     * True when the developer has unsaved changes to the app's API scopes or webhook events:
     * saving would create a new scopes version for review.
     */
    @computed
    get hasPendingScopesVersion(): boolean {
        const currentWebhookEventNames = new Set(
            this.form.$.webhookEvents.value.map(event => event.name),
        );
        return (
            !setsEqual(this.form.$.authScopes.value, this.savedAuthScopes) ||
            !setsEqual(currentWebhookEventNames, this.savedWebhookEventNames)
        );
    }

    @computed
    get selectedApiGroupHasBlockedAuthScopes(): boolean {
        const selectedApiGroup = this.selectedApiGroup;
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            selectedApiGroup !== undefined &&
            selectedApiGroup.scopeGroups.some(scopeGroup =>
                scopeGroup.scopes.some(
                    scope =>
                        (scope.hasRead && !this.enabledAuthScopes.has(scope.name + ':r')) ||
                        (scope.hasWrite && !this.enabledAuthScopes.has(scope.name + ':w')),
                ),
            )
        );
    }

    @computed
    get selectedApiGroupHasWebhooks(): boolean {
        const selectedApiGroup = this.selectedApiGroup;
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            selectedApiGroup !== undefined &&
            selectedApiGroup.scopeGroups.some(scopeGroup => scopeGroup.webhookEvents.length > 0)
        );
    }

    @computed
    get scopeDisplayNames(): { read: string; write: string } {
        const authScopes = [...this.form.$.authScopes.value];
        authScopes.sort();
        return this.getApiScopeDisplayNames(authScopes);
    }

    @computed
    get webhookEventDisplayNames(): string {
        const webhookEventNames = this.form.$.webhookEvents.value.map(event => event.name);
        webhookEventNames.sort();
        return getDisplayNamesString(
            webhookEventNames.map(eventName => this.webhookEventMap.get(eventName)!),
        );
    }

    @action
    async load(id: number): Promise<void> {
        this.id = id;
        this.publicId = '';
        this.scopesVersions = [];
        this.savedAuthScopes = new Set<string>();
        this.savedWebhookEventNames = new Set<string>();
        this.applicationKey1 = '';
        this.form.reset();
        this.deleted = false;
        this.approvalStatus = AppApprovalStatus.NotStarted;
        this.deleteDialogVisible = false;
        this.errorBannerVisible = false;
        this.loading = true;
        try {
            const config = await this.appApi.getApiConfiguration();
            const model =
                id > 0 ? await this.appApi.getApp(id, { includeApiScopeGroups: true }) : null;

            runInAction(() => {
                this.developerType = config.developerType;
                this.appCategories = config.appCategories;
                this.apiGroups =
                    id > 0
                        ? [
                              {
                                  ...config.apiGroups.find(
                                      apiGroup => apiGroup.name === model!.apiGroupName,
                                  )!,
                                  scopeGroups: model!.apiScopeGroups,
                              },
                          ]
                        : config.apiGroups;
                this.apiScopeMap = new Map<string, ApiScope>(
                    this.apiGroups
                        .map(apiGroup => apiGroup.scopeGroups.map(scopeGroup => scopeGroup.scopes))
                        .flat(2)
                        .map(scope => [scope.name, scope]),
                );
                this.webhookEventMap = new Map<string, WebhookEvent>(
                    this.apiGroups
                        .map(apiGroup =>
                            apiGroup.scopeGroups.map(scopeGroup => scopeGroup.webhookEvents),
                        )
                        .flat(2)
                        .map(event => [event.name, event]),
                );

                this.enabledAuthScopes = new Set<string>(
                    this.apiGroups
                        .map(apiGroup =>
                            apiGroup.scopeGroups.map(scopeGroup =>
                                scopeGroup.scopes.map(scope => [
                                    ...(scope.canRead ? [scope.name + ':r'] : []),
                                    ...(scope.canWrite ? [scope.name + ':w'] : []),
                                ]),
                            ),
                        )
                        .flat(3),
                );
                if (model) {
                    this.publicId = model.publicId;
                    this.scopesVersions = model.scopesVersions.map(createAppScopesVersionViewModel);
                    this.applicationKey1 = model.applicationKey1;
                    this.form.$.appName.reset(model.appName);
                    this.form.$.organizationName.reset(model.organizationName);
                    this.form.$.homepageUrl.reset(model.homepageUrl);
                    this.form.$.emailAddress.reset(model.emailAddress ?? '');
                    this.form.$.description.reset(model.description ?? '');
                    this.form.$.appCategoryId.reset(model.appCategoryId);
                    this.form.$.appBuilder.reset(model.appBuilder);
                    this.form.$.externalDataGuid.reset(model.externalDataGuid);
                    const existingAuthScopes = new Set<string>(model.authScopes);
                    this.existingBlockedAuthScopes = existingAuthScopes.difference(
                        this.enabledAuthScopes,
                    );
                    this.enabledAuthScopes = this.enabledAuthScopes.union(existingAuthScopes);
                    this.handleApiGroupChange({
                        text: this.apiGroups.find(apiGroup => apiGroup.name === model.apiGroupName)!
                            .displayName,
                        value: model.apiGroupName,
                    });
                    this.form.$.authScopes.reset(existingAuthScopes);
                    this.form.$.webhookEvents.reset(model.webhookEvents);
                    this.savedAuthScopes = new Set<string>(existingAuthScopes);
                    this.savedWebhookEventNames = new Set<string>(
                        model.webhookEvents.map(event => event.name),
                    );
                    this.form.$.webhookSignatureKey.reset(model.webhookSignatureKey ?? '');
                    this.isWebhookSignatureKeyReadonly =
                        !!model.webhookSignatureKey && model.webhookSignatureKey.length > 0;
                    this.form.$.appAvailabilities.reset(
                        model.appAvailabilities.length > 0
                            ? model.appAvailabilities
                            : [EmptyAppAvailability],
                    );
                    this.form.$.networkAppAvailabilities.reset(
                        model.networkAppAvailabilities.length > 0
                            ? model.networkAppAvailabilities
                            : [EmptyAppAvailability],
                    );
                    this.form.$.secretManagementOption.reset(model.secretManagementOption);
                    this.deleted = model.deleted;
                    this.approvalStatus = model.approvalStatus;
                } else {
                    this.approvalStatus =
                        this.developerType === DeveloperType.Customer &&
                        config.autoApproveCustomerApps
                            ? AppApprovalStatus.Approved
                            : AppApprovalStatus.NotStarted;
                    this.existingBlockedAuthScopes = new Set<string>();
                    if (this.apiGroups.length === 1) {
                        // For the user's convenience, automatically select the only option:
                        this.handleApiGroupChange({
                            text: this.apiGroups[0].displayName,
                            value: this.apiGroups[0].name,
                        });
                    }
                }
                this.loading = false;
            });
        } catch {
            runInAction(() => {
                this.errorBannerVisible = true;
                this.loading = false;
            });
        }
    }

    @action.bound
    handleGenerateExternalDataGuid(): void {
        // eslint-disable-next-line no-restricted-globals
        this.form.$.externalDataGuid.reset(crypto.randomUUID());
    }

    @action.bound
    handleApiGroupChange(option: DropdownOption<string>): void {
        this.form.$.apiGroup.onChange(option);
        this.form.$.authScopes.reset(new Set<string>());
        this.form.$.webhookEvents.reset([]);
        this.form.$.webhookSignatureKey.reset('');
        if (!option) {
            return;
        }

        const selectedApiGroup = this.apiGroups.find(apiGroup => apiGroup.name === option.value)!;
        const defaultResourceOwners =
            selectedApiGroup.assignedResourceOwners.length > 0
                ? selectedApiGroup.assignedResourceOwners
                : [''];
        this.form.$.appAvailabilities.reset(
            defaultResourceOwners.map(resourceOwner => ({ resourceOwner, note: '' })),
        );
        this.form.$.networkAppAvailabilities.reset();
    }

    @action.bound
    handleDeleteWebhookSignatureKey(): void {
        this.form.$.webhookSignatureKey.reset('');
        this.isWebhookSignatureKeyReadonly = false;
    }

    @action.bound
    handleAddResourceOwner(): void {
        const fieldState = this.form.$.appAvailabilities;
        fieldState.value = [...fieldState.value, EmptyAppAvailability];
    }

    @action.bound
    handleAddNetwork(): void {
        const fieldState = this.form.$.networkAppAvailabilities;
        fieldState.value = [...fieldState.value, EmptyAppAvailability];
    }

    @action.bound
    handleDelete(): void {
        this.deleteDialogVisible = true;
    }

    @action.bound
    handleCancelDelete(): void {
        this.deleteDialogVisible = false;
    }

    @action
    async deleteApp(): Promise<void> {
        this.deleteDialogVisible = false;
        this.deleting = true;
        try {
            await this.appApi.deleteApp(this.id);
            this.notificationCenterStore.addSnackbar('App deleted.');
        } finally {
            runInAction(() => {
                this.deleting = false;
            });
        }
    }

    @action
    async saveNewApp(): Promise<string> {
        const result = await this.form.enableAutoValidationAndValidate();
        if (result.hasError) {
            return '';
        }

        runInAction(() => {
            this.saving = true;
        });
        try {
            const url = await this.appApi.createApp(this.createAppDetailsModel());
            runInAction(() => {
                this.appJustCreated = true;
            });
            return url;
        } catch {
            return '';
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    }

    @action
    async saveExistingApp(): Promise<boolean> {
        const result = await this.form.enableAutoValidationAndValidate();
        if (result.hasError) {
            return false;
        }

        if (this.confirmScopeRemovalDialogVisible) {
            this.confirmScopeRemovalDialogVisible = false;
        } else {
            const removedExistingBlockedAuthScopes = this.existingBlockedAuthScopes.difference(
                result.value.authScopes.$,
            );
            if (removedExistingBlockedAuthScopes.size > 0) {
                this.removedExistingBlockedAuthScopes = this.getApiScopeDisplayNames(
                    Array.from(removedExistingBlockedAuthScopes),
                );
                this.confirmScopeRemovalDialogVisible = true;
                return false;
            }
        }

        runInAction(() => {
            this.saving = true;
        });
        try {
            await this.appApi.updateApp(this.id, this.createAppDetailsModel());
            this.notificationCenterStore.addSnackbar('App saved.');
            return true;
        } catch {
            return false;
        } finally {
            runInAction(() => {
                this.saving = false;
            });
        }
    }

    private createAppDetailsModel(): AppDetailsModel {
        const getAppAvailabilities = (appAvailabilities: AppAvailability[]) =>
            appAvailabilities
                .map(item => ({
                    resourceOwner: item.resourceOwner.trim(),
                    note: item.note.trim(),
                }))
                .filter(appAvailability => appAvailability.resourceOwner);
        const $ = this.form.$;
        return {
            appName: $.appName.$.trim(),
            organizationName: $.organizationName.$.trim(),
            homepageUrl: $.homepageUrl.$.trim(),
            emailAddress: $.emailAddress.$.trim() || null,
            description: $.description.$,
            appCategoryId: $.appCategoryId.$,
            appBuilder: this.developerType === DeveloperType.Customer ? $.appBuilder.$ : null,
            externalDataGuid: $.externalDataGuid.$.trim().toLowerCase(),
            apiGroupName: $.apiGroup.$!.value!,
            authScopes: Array.from($.authScopes.$),
            webhookEvents: $.webhookEvents.$,
            webhookSignatureKey: this.isWebhookSignatureKeyReadonly
                ? null
                : $.webhookSignatureKey.$,
            appAvailabilities: getAppAvailabilities($.appAvailabilities.$),
            networkAppAvailabilities: getAppAvailabilities($.networkAppAvailabilities.$),
            // When creating a new Partner App, default the secretManagementOption to ResourceOwner
            secretManagementOption:
                this.id > 0 || $.apiGroup.$!.value === 'tenant'
                    ? $.secretManagementOption.$
                    : SecretManagementOption.ResourceOwner,
        };
    }

    private getApiScopeDisplayNames(authScopes: string[]): { read: string; write: string } {
        return {
            read: getDisplayNamesString(
                authScopes
                    .filter(authScope => authScope.endsWith(':r'))
                    .map(authScope => this.apiScopeMap.get(authScope.slice(0, -2))!),
            ),
            write: getDisplayNamesString(
                authScopes
                    .filter(authScope => authScope.endsWith(':w'))
                    .map(authScope => this.apiScopeMap.get(authScope.slice(0, -2))!),
            ),
        };
    }
}
